/**
 * Offline-first sync manager.
 *
 * localStorage is the primary store (via Zustand persist).
 * This manager watches for dirty state and syncs to the API in the background.
 *
 * Strategy:
 *  - Debounced auto-save: waits 2s after last change before syncing
 *  - Online/offline awareness: queues saves when offline, flushes on reconnect
 *  - Conflict resolution: last-write-wins with updatedAt timestamp
 *  - Exponential backoff on errors: 30s → 60s → 120s → max 5min
 */

import { useProjectStore } from '../stores/projectStore';
import { updateProject, ApiError } from './api-client';

const isDevMode = !import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const SAVE_DEBOUNCE_MS = 2000;
const ERROR_AUTO_CLEAR_MS = 10_000;
const INITIAL_RETRY_MS = 30_000;
const MAX_RETRY_MS = 300_000; // 5 minutes

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let errorClearTimer: ReturnType<typeof setTimeout> | null = null;
let isSyncing = false;
let pendingSave = false;
let retryDelayMs = INITIAL_RETRY_MS;

/** Sync status for UI display */
export type SyncStatus = 'idle' | 'saving' | 'saved' | 'offline' | 'error';

/** Listeners for sync status changes */
const statusListeners = new Set<(status: SyncStatus, error?: string) => void>();
let currentStatus: SyncStatus = 'idle';

function setStatus(status: SyncStatus, error?: string): void {
  currentStatus = status;
  for (const listener of statusListeners) {
    listener(status, error);
  }
}

/** Subscribe to sync status changes */
export function onSyncStatus(listener: (status: SyncStatus, error?: string) => void): () => void {
  statusListeners.add(listener);
  // Immediately notify current status
  listener(currentStatus);
  return () => statusListeners.delete(listener);
}

/** Clear any pending retry timer */
function clearRetryTimer(): void {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

/** Clear error auto-clear timer */
function clearErrorClearTimer(): void {
  if (errorClearTimer) {
    clearTimeout(errorClearTimer);
    errorClearTimer = null;
  }
}

/** Schedule an auto-clear of error status */
function scheduleErrorAutoClear(): void {
  clearErrorClearTimer();
  errorClearTimer = setTimeout(() => {
    if (currentStatus === 'error') {
      setStatus('idle');
    }
  }, ERROR_AUTO_CLEAR_MS);
}

/** Schedule a retry with exponential backoff */
function scheduleRetry(): void {
  clearRetryTimer();
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (pendingSave) {
      pendingSave = false;
      doSave();
    }
  }, retryDelayMs);
  // Increase backoff for next failure (capped at MAX_RETRY_MS)
  retryDelayMs = Math.min(retryDelayMs * 2, MAX_RETRY_MS);
}

/** Perform the actual save to API */
async function doSave(): Promise<void> {
  const { project } = useProjectStore.getState();
  if (!project || isDevMode) return;

  if (isSyncing) {
    pendingSave = true;
    return;
  }

  if (!navigator.onLine) {
    setStatus('offline');
    pendingSave = true;
    return;
  }

  isSyncing = true;
  setStatus('saving');
  clearErrorClearTimer();

  try {
    await updateProject(project.id, {
      name: project.name,
      data: {
        name: project.name,
        framedArtworks: project.framedArtworks,
      },
    });

    useProjectStore.setState({ isDirty: false });
    setStatus('saved');

    // Reset retry backoff on success
    retryDelayMs = INITIAL_RETRY_MS;
    clearRetryTimer();

    // Auto-clear "saved" status after 3s
    setTimeout(() => {
      if (currentStatus === 'saved') setStatus('idle');
    }, 3000);
  } catch (err) {
    const message = err instanceof ApiError ? err.message : 'Sync failed';
    setStatus('error', message);
    pendingSave = true;

    // Auto-clear error status after 10s
    scheduleErrorAutoClear();

    // Schedule retry with exponential backoff
    scheduleRetry();
  } finally {
    isSyncing = false;

    // If another save was requested while we were syncing (and no retry scheduled), do it now
    if (pendingSave && !retryTimer) {
      pendingSave = false;
      scheduleSave();
    }
  }
}

/** Schedule a debounced save */
function scheduleSave(): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    doSave();
  }, SAVE_DEBOUNCE_MS);
}

/** Initialize the sync manager — call once at app startup */
export function initSyncManager(): () => void {
  if (isDevMode) return () => {};

  // Watch for dirty state changes
  const unsubStore = useProjectStore.subscribe((state, prevState) => {
    if (state.isDirty && !prevState.isDirty) {
      // Clear error on new user edit
      if (currentStatus === 'error') {
        setStatus('idle');
        clearErrorClearTimer();
      }
      scheduleSave();
    }
    // Also schedule on any project data change when already dirty
    if (state.isDirty && state.project !== prevState.project) {
      if (currentStatus === 'error') {
        setStatus('idle');
        clearErrorClearTimer();
      }
      scheduleSave();
    }
  });

  // Listen for online/offline events
  const handleOnline = (): void => {
    if (pendingSave) {
      pendingSave = false;
      clearRetryTimer();
      retryDelayMs = INITIAL_RETRY_MS;
      scheduleSave();
    } else if (currentStatus === 'offline') {
      setStatus('idle');
    }
  };

  const handleOffline = (): void => {
    setStatus('offline');
    clearRetryTimer();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Set initial status
  if (!navigator.onLine) {
    setStatus('offline');
  }

  // Cleanup
  return () => {
    unsubStore();
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    if (saveTimer) clearTimeout(saveTimer);
    clearRetryTimer();
    clearErrorClearTimer();
  };
}
